import * as MUI from '@mui/material'
import { Loading, SIZES } from 'components/loading/component'
import * as Dropdown from 'components/dropdown'
import * as Header from 'components/header'
import * as Theme from 'components/charts/theme'
import React from 'react'
import * as Elasticsearch from 'services/elasticsearch'
import * as V from 'victory'

import * as Actions from './actions'
import * as Constants from './constants'
import * as Types from './types'
import './styles.scss'
import * as Utils from './utils'

const editors = (
  state: Types.ViewState,
  dateChanged: any,
  intervalChanged: any,
  displayChanged: any) => {
  const className = 'horizontal'
  return (
  <div className="engagement-selectors">
    <div className="dropdown-inline">
      <Dropdown.DropdownComponent data={Constants.DateRangesAsDropdownOptions}
                          className={className}
                          selected={state.selectedDateRange.key}
                          onChange={dateChanged}/>
      <Dropdown.DropdownComponent data={Elasticsearch.AggregateIntervalOptions}
                          className={className}
                          selected={state.selectedInterval.key}
                          onChange={intervalChanged}/>
      <Dropdown.DropdownComponent data={Types.DisplayOptions}
                          className={className}
                          selected={state.selectedDisplay.key}
                          onChange={displayChanged}/>
    </div>
  </div>
  )

}

interface ChartData {
  label: string,
  timestamp: any,
  click: number,
  open: number,
}

const chartTooltip = (view: Types.ViewState, clickKey: string, openKey: string, data: Types.OpenClickPoint): string => {
  if ( isRate(view.selectedDisplay) ) {
    return `${data.timestamp}:\nOpens: ${data[openKey].toFixed(2)} %\nClicks: ${data[clickKey].toFixed(2)} %`
  } else {
    return `${data.timestamp}:\nOpens: ${data[openKey]}\nClicks: ${data[clickKey]}`
  }
}

interface LegendData {
  name: string,
  symbol: any,
}

const CLICK_COLOR = '#319699'
const OPEN_COLOR = '#4ecdc4'

const legendData = (view: Types.ViewState): LegendData[] => {
  return [
    {
      name: chartLegend(view, 'Click'),
      symbol: {
        fill: CLICK_COLOR,
      }
    },
    {
      name: chartLegend(view, 'Open'),
      symbol: {
        fill: OPEN_COLOR,
      }
    }
  ]

}

const isUnique = (option: Dropdown.DropdownOption): boolean => {
  return [Types.DisplayOptionTypes.UniqueCounts, Types.DisplayOptionTypes.UniqueRates].includes(option.value)
}

const isRate = (option: Dropdown.DropdownOption): boolean => {
  return [Types.DisplayOptionTypes.UniqueRates, Types.DisplayOptionTypes.TotalRates].includes(option.value)
}

const chartKey = (view: Types.ViewState, label: string): string => {
  const uniqueOrTotal = isUnique(view.selectedDisplay) ? 'unique' : 'total'
  const countOrRate = isRate(view.selectedDisplay) ? 'Rate' : ''
  return `${uniqueOrTotal}${label}${countOrRate}`
}

const chartLegend = (view: Types.ViewState, label: string): string => {
  const uniqueOrTotal = isUnique(view.selectedDisplay.value) ? 'Unique' : 'Total'
  const countOrRate = isRate(view.selectedDisplay.value) ? '%' : 'Count'
  return `${uniqueOrTotal} ${label} ${countOrRate}`
}

const buildChartData = (data: Types.OpenClickPoint[], view: Types.ViewState): ChartData[] => {
  const clickKey = chartKey(view, 'Click')
  const openKey = chartKey(view, 'Open')
  return data.map((d: Types.OpenClickPoint) => {
    return {
      label: chartTooltip(view, clickKey, openKey, d),
      timestamp: d.timestamp,
      click: d[clickKey] || 0,
      open: d[openKey] || 0,
    }
  })
}

const header = () => {
  return <Header.HeaderComponent icon={Header.HeaderIcons.OpensClicks}
                                 subTitle="Opens & Clicks"
                                 title="Analytics"/>
}

const chartTheme = Theme.theme({
  height: 400,
  width: 500,
  padding: 50,
})

const chart = (data: Types.OpenClickPoint[], view: Types.ViewState) => {
  const chartData = buildChartData(data, view)
  const maxY = Math.max(
    ...chartData.map((c) => Math.max(c.click, c.open))
  )
  const clicks = chartData.map((c) => ({
    x: c.timestamp,
    y: c.click,
    label: c.label,
  }))
  const opens = chartData.map((c) => ({
    x: c.timestamp,
    y: c.open,
    label: c.label,
  }))
  return (
    <div className="opens-clicks-chart">
      <V.VictoryChart containerComponent={
                        <V.VictoryVoronoiContainer
                          labels={({ datum }) => datum.label}
                      />}
                      theme={chartTheme}
                      domain={{y: [0, maxY+10]}}
                      animate={{
                        duration: 2000,
                        onLoad: { duration: 1000 }
                      }}>
        <V.VictoryLegend orientation="horizontal" x={100} gutter={20} borderPadding={{left: 10, right: 10}} style={{
          border: { stroke: 'black' }, title: {fontSize: 20 }
        }} data={legendData(view)}/>
        <V.VictoryLine data={clicks} labelComponent={<V.VictoryTooltip/>} style={{
          data: { stroke: CLICK_COLOR }
        }}/>
        <V.VictoryLine data={opens} labelComponent={<V.VictoryTooltip/>} style={{
          data: { stroke: OPEN_COLOR }
        }}/>
        <V.VictoryAxis crossAxis standalone={false} fixLabelOverlap={true}/>
        <V.VictoryAxis dependentAxis crossAxis standalone={false} />
      </V.VictoryChart>

    </div>
  )
}

const grid = (data: Types.OpenClickPoint[]) => {
  return (
    <div className="engagement-table">
      <MUI.TableContainer component={MUI.Paper}>
        <MUI.Table>
          <MUI.TableHead>
            <MUI.TableRow>
              <MUI.TableCell>Date</MUI.TableCell>
              <MUI.TableCell>Deliveries</MUI.TableCell>
              <MUI.TableCell>Total Clicks</MUI.TableCell>
              <MUI.TableCell>%</MUI.TableCell>
              <MUI.TableCell>Total Opens</MUI.TableCell>
              <MUI.TableCell>%</MUI.TableCell>
              <MUI.TableCell>Unique Clicks</MUI.TableCell>
              <MUI.TableCell>%</MUI.TableCell>
              <MUI.TableCell>Unique Opens</MUI.TableCell>
              <MUI.TableCell>%</MUI.TableCell>
            </MUI.TableRow>
          </MUI.TableHead>
          <tbody>
            {data.map((d: Types.OpenClickPoint) => {
              return <MUI.TableRow key={d.timestamp} className="data">
                <MUI.TableCell>{d.timestamp}</MUI.TableCell>
                <MUI.TableCell>{d.totalDelivered}</MUI.TableCell>
                <MUI.TableCell>{d.totalClick}</MUI.TableCell>
                <MUI.TableCell>{d.totalClickRate.toFixed(2)}</MUI.TableCell>
                <MUI.TableCell>{d.totalOpen}</MUI.TableCell>
                <MUI.TableCell>{d.totalOpenRate.toFixed(2)}</MUI.TableCell>
                <MUI.TableCell>{d.uniqueClick}</MUI.TableCell>
                <MUI.TableCell>{d.uniqueClickRate.toFixed(2)}</MUI.TableCell>
                <MUI.TableCell>{d.uniqueOpen}</MUI.TableCell>
                <MUI.TableCell>{d.uniqueOpenRate.toFixed(2)}</MUI.TableCell>
              </MUI.TableRow>

            })}
          </tbody>
        </MUI.Table>
      </MUI.TableContainer>
    </div>
  )
}

interface DashboardProps {
  display?: string
}

export const OpensClicksDashboard = (props: DashboardProps) => {

  const [data, setData] = React.useState([])
  const [view, setView] = React.useState(Types.emptyState)
  const [loading, setLoading] = React.useState(true)

  const showChart = (): boolean => {
    return !props.display || props.display !== Types.DisplayType.Table
  }

  const showGrid = (): boolean => {
    return !props.display || props.display !== Types.DisplayType.Chart
  }

  const viewPropertyChanged = (key: string, e: Dropdown.OnChangeEvent) => {
    const newView: Types.ViewState = {
      ...view,
      [key]: e.selected,
    }
    setView(newView)
    fetchStats(newView)
  }

  const intervalChanged = (e: Dropdown.OnChangeEvent) => viewPropertyChanged('selectedInterval', e)
  const dateRangeChanged = (e: Dropdown.OnChangeEvent) => viewPropertyChanged('selectedDateRange', e)
  const displayChanged = (e: Dropdown.OnChangeEvent) => viewPropertyChanged('selectedDisplay', e)

  const fetchStats = (viewData: Types.ViewState) => {
    setLoading(true)
    Actions.fetchStatsData(viewData).then((stats: Elasticsearch.AggregationResponsePayload) => {
      setData(Utils.parseStats(stats))
      setLoading(false)
    })
  }

  React.useEffect(() => fetchStats(view), [])

  return <div className="engagement">
    {header()}
    <MUI.Box  sx={{
      display: 'flex',
      flexDirection: 'row',
      flexWrap: 'wrap',
      minWidth: '500px',
      justifyContent: 'left',
      alignItems: 'flex-start',
      p: 1,
      m: 1,
      bgcolor: 'background.paper',
      borderRadius: 1,
    }}>
    { editors(view, dateRangeChanged, intervalChanged, displayChanged)}
    { loading ? <div className="loading"><Loading size={SIZES.LARGE} withLogo={false}/></div> : <div>
      { showChart() && chart(data, view) }
      { showGrid() && grid(data) }
    </div> }
    </MUI.Box>
  </div>
}
